import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { loadGoogleMaps } from "../../lib/maps";
import { MapPin, Navigation, Phone, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";

const dirUrl = (o) =>
  o.delivery_location
    ? `https://www.google.com/maps/dir/?api=1&destination=${o.delivery_location.lat},${o.delivery_location.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${o.address} ${o.city_name || ""}`)}`;

function RunMap({ apiKey, orders }) {
  const el = useRef(null);
  const [status, setStatus] = useState("loading");
  const pinned = orders.filter((o) => o.delivery_location);

  useEffect(() => {
    if (!apiKey || pinned.length === 0) { setStatus("empty"); return; }
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !el.current) return;
        const map = new google.maps.Map(el.current, {
          zoom: 12, mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
        });
        const bounds = new google.maps.LatLngBounds();
        pinned.forEach((o, i) => {
          const pos = { lat: o.delivery_location.lat, lng: o.delivery_location.lng };
          const m = new google.maps.Marker({
            position: pos, map, title: `${i + 1}. ${o.recipient_name}`,
            label: { text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },
            icon: { url: "/rose-pin.png", scaledSize: new google.maps.Size(40, 40), anchor: new google.maps.Point(20, 38),
                    labelOrigin: new google.maps.Point(20, 14) },
          });
          m.addListener("click", () => window.open(dirUrl(o), "_blank"));
          bounds.extend(pos);
        });
        map.fitBounds(bounds, 60);
        if (pinned.length === 1) map.setZoom(15);
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("error"));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, orders]);

  if (status === "error" || status === "empty") return null;
  return (
    <div className="relative rounded-sm overflow-hidden border border-brass/30" style={{ height: 240 }} data-testid="courier-map">
      <div ref={el} className="w-full h-full" />
      {status === "loading" ? (
        <div className="absolute inset-0 flex items-center justify-center bg-plaster/70"><Loader2 className="animate-spin text-brass" /></div>
      ) : null}
    </div>
  );
}

export default function CourierRun() {
  const [data, setData] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [date, setDate] = useState("");

  const load = (d) => api.get("/florist/courier/today", { params: d ? { date: d } : {} })
    .then((r) => setData(r.data)).catch(() => setData({ orders: [], count: 0, pinned: 0 }));

  useEffect(() => {
    api.get("/bootstrap").then((r) => setApiKey(r.data?.maps?.google_maps_api_key || "")).catch(() => {});
    load();
    const id = setInterval(() => load(date), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const orders = data?.orders || [];

  return (
    <div className="space-y-4" data-testid="courier-run">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-ink">Livraisons du jour</h2>
          <p className="text-xs text-muted">
            {data ? `${data.count} livraison(s) · ${data.pinned} localisée(s) au pin` : "…"}
          </p>
        </div>
        <input type="date" data-testid="courier-date" className="field w-auto text-sm"
               value={date} onChange={(e) => setDate(e.target.value)} />
      </div>

      {apiKey && data?.pinned > 0 ? <RunMap apiKey={apiKey} orders={orders} /> : null}

      {orders.length === 0 ? (
        <p className="text-sm text-muted py-8" data-testid="courier-empty">Aucune livraison active pour cette date.</p>
      ) : null}

      {orders.map((o, i) => (
        <article key={o.id} className="card p-4 space-y-3" data-testid={`courier-stop-${o.order_number}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-ink text-plaster text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
              <div>
                <p className="text-sm text-ink font-medium">{o.recipient_name}</p>
                <p className="text-xs text-muted">{o.order_number} · {o.delivery_window_label}</p>
              </div>
            </div>
            <span className="text-xs px-2 py-1 border border-brass-light text-muted whitespace-nowrap">{o.status_label}</span>
          </div>

          <div className="text-sm text-ink space-y-0.5">
            <p className="flex items-start gap-1.5"><MapPin size={14} className="text-brass mt-0.5 flex-shrink-0" /> {o.address}{o.zone_name ? ` · ${o.zone_name}` : ""}</p>
            {o.landmark_note ? <p className="text-xs text-muted ps-5">Repère : {o.landmark_note}</p> : null}
            {o.delivery_location ? (
              <p className="text-xs text-ink ps-5 flex items-center gap-1"><CheckCircle2 size={12} className="text-brass" /> Localisation exacte partagée</p>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <a href={dirUrl(o)} target="_blank" rel="noreferrer" data-testid={`courier-navigate-${o.order_number}`}
               className="btn-primary text-xs flex items-center gap-1.5 py-2">
              <Navigation size={14} /> Naviguer
            </a>
            {o.call_before_delivery && o.recipient_phone ? (
              <a href={`tel:${o.recipient_phone}`} className="btn-secondary text-xs flex items-center gap-1.5 py-2"
                 data-testid={`courier-call-${o.order_number}`}><Phone size={14} /> Appeler</a>
            ) : null}
            {o.recipient_whatsapp ? (
              <a href={`https://wa.me/${o.recipient_whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer"
                 className="btn-secondary text-xs flex items-center gap-1.5 py-2"
                 data-testid={`courier-whatsapp-${o.order_number}`}><MessageCircle size={14} /> WhatsApp</a>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
}
